import { useMemo } from "react";

import style from "./IconControls.module.scss";

import EyeSlashIcon from "@/Common/_images/Icons/EyeSlash.svg";
import RBAC_DeleteIcon from "@/Common/_images/RBAC_DeleteIcon.svg";
import Crayon from "@/Globals/_images/icons/Crayon.svg";
import CheckSquareFill from "../../_images/check-square-fill.svg";
import MinusSquareFill from "../../_images/minus-square-fill.svg";
import SquareRegular from "../../_images/square-regular.svg";

type IconType =
    "question-circle"
    | "pen"
    | "remove"
    | "search"
    | "exclamation-circle"
    | "checkmark"
    | "chevron-down"
    | "chevron-up"
    | "eye-slash"
    | "chevron-up"
    | "check-square-fill"
    | "minus-square-fill"
    | "square-regular"
    | "delete-icon";

type Color =
    "orange"
    | "darkblue"
    | "red"
    | "green"
    | "lightblue"
    | "green"
    | "blue";

interface IconRenderSetting {
    className?: string;
    getComponent?: () => JSX.Element
}

const iconSettings: Record<string, IconRenderSetting> = {
    "question-circle": { className: "fa fa-question-circle" },
    "pen": { getComponent: () => <Crayon /> },
    "remove": { className: "fa fa-times" },
    "search": { className: "fa fa-search" },
    "exclamation-circle": { className: "fa fa-exclamation-circle" },
    "checkmark": { className: "fa fa-check" },
    "chevron-down": { className: "fa fa-chevron-down" },
    "chevron-up": { className: "fa fa-chevron-up" },
    "eye-slash": { getComponent: () => <EyeSlashIcon /> },
    "check-square-fill": { getComponent: () => <CheckSquareFill /> },
    "minus-square-fill": { getComponent: () => <MinusSquareFill /> },
    "square-regular": { getComponent: () => <SquareRegular /> },
    "delete-icon": { getComponent: () => <RBAC_DeleteIcon /> }
};

const iconColor: Record<Color, string> = {
    "orange": style.orange,
    "darkblue": style.darkblue,
    "red": style.red,
    "green": style.green,
    "blue": style.blue,
    "lightblue": style.lightblue
};

function GetIconSettings(type: IconType): IconRenderSetting {
    return iconSettings[type] ?? { className: "" };
}

function GetIconColor(color: Color): string {
    return iconColor[color] ?? "";
}

interface IIconProps {
    type: IconType;
    color?: Color;
    title?: string;
    iconHeight?: string;
    iconWidth?: string;
    className?: string;
}

function Icon(props: IIconProps): JSX.Element {
    const settings = useMemo(() => GetIconSettings(props.type), [props.type]);
    const color = useMemo(() => GetIconColor(props.color), [props.color]);

    let component: JSX.Element = null;
    let styles: React.CSSProperties = {};

    if (props.iconWidth)
        styles.width = props.iconWidth;
    if (props.iconHeight)
        styles.height = props.iconHeight;

    if (settings.getComponent) {
        component = (
            <div className={`${style.svg} ${color}`.trim()}>
                {settings.getComponent()}
            </div>
        );
    }
    else {
        component = (
            <i
                className={`${settings.className} ${color} ${props.className}`.trim()}
                title={props.title}
            />
        );
    }

    return (
        <div className={style.iconDefault} style={styles}>
            {component}
        </div>
    );
}

interface IIconLinkProps extends IIconProps {
    onClick: () => void;
}

function IconLink(props: IIconLinkProps): JSX.Element {
    return (
        <a className={`${style.iconDefault} ${style.iconLink}`}>
            <Icon {...props} />
        </a>
    );
}

interface IIconControlButtonProps extends IIconProps {
    id?: string;
    onClick: () => void;
    color?: Color;
    buttonClassName?: string;
    isDisabled?: boolean;
    dataCy?: string;
}

function IconButton(props: IIconControlButtonProps): JSX.Element {
    return (
        <button
            id={props.id}
            data-cy={props.dataCy}
            className={`btn btn-default ${style.defaultButton} ${props.buttonClassName ?? ""}`}
            onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                props.onClick();
            }}
            disabled={props.isDisabled}
        >
            <Icon {...props} />
        </button>
    );
}

export { Icon, IconButton, IconLink };
